import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useIsDevice } from '../../hooks/useIsDevice';
import { getUnreadCount } from '../../services/chatServices';
import { routes } from '../../lib/routes';
import {
  ChatLines,
  HomeSimple,
  LogOut,
  Settings,
  UnionHorizAlt,
  Xmark,
  Search,
  User,
} from 'iconoir-react';
import { sidebarStyles } from '../../styles/style';
import { useBranding, SponsorLine } from '../../contexts/BrandingContext';
import Avatar from './Avatar';
import { useAuth } from '../../contexts/AuthContext';

interface SidebarProps {
  menuOpen: boolean;
  setMenuOpen: (menuOpen: boolean) => void;
}

const Sidebar = ({ menuOpen, setMenuOpen }: SidebarProps) => {
  const navigate = useNavigate();
  const { logoDark, name: brandName } = useBranding(); // V1.5.M10 B-P1
  const { pathname } = useLocation();
  const { isMobile, isTablet } = useIsDevice();
  const { user, logout } = useAuth();

  const pathnameRef = useRef(pathname);

  const sidebar = [
    {
      label: 'Dashboard',
      path: routes.home,
      icon: <HomeSimple className='w-6 h-6 me-4' />,
      activeIcon: <HomeSimple className='w-6 h-6 me-4' />,
    },
    {
      label: 'Discover',
      path: routes.discover,
      icon: <Search className='w-6 h-6 me-4' />,
      activeIcon: <Search className='w-6 h-6 me-4' />,
      tourId: 'dashboard-discover',
    },
    {
      label: 'Matches',
      path: routes.matches,
      icon: <UnionHorizAlt className='w-6 h-6 me-4' />,
      activeIcon: <UnionHorizAlt className='w-6 h-6 me-4' />,
      tourId: 'dashboard-matches',
    },
    {
      label: 'Inbox',
      path: routes.chat,
      icon: <ChatLines className='w-6 h-6 me-4' />,
      activeIcon: <ChatLines className='w-6 h-6 me-4' />,
      tourId: 'chat-list',
    },
    {
      label: 'Profile',
      path: routes.profile,
      icon: <User className='w-6 h-6 me-4' />,
      activeIcon: <User className='w-6 h-6 me-4' />,
      tourId: 'dashboard-profile',
    },
    {
      label: 'Settings',
      path: routes.settings,
      icon: <Settings className='w-6 h-6 me-4' />,
      activeIcon: <Settings className='w-6 h-6 me-4' />,
    },
  ] as Array<{
    label: string;
    path: string;
    icon: React.ReactNode;
    activeIcon: React.ReactNode;
    tourId?: string;
  }>;

  // Track the number of unread messages
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    // Fetch unread count on mount and poll every 30s
    const fetchUnread = () => {
      getUnreadCount().then(setUnreadCount).catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  const handleLogoRoute = () => {
    if (pathname.includes(routes.onboarding)) {
      return;
    }
    return navigate(routes.home);
  };

  const baseClassName = `${sidebarStyles.base} ${(isMobile || isTablet) && (menuOpen ? sidebarStyles.mobile.open : sidebarStyles.mobile.closed)}`;

  return (
    <div className={baseClassName}>
      <div className='px-8 mb-12 flex items-center justify-center gap-4 relative'>
        {!isMobile ? (
          <div className='flex flex-col items-center'>
            {/* The logo navigates home, so it needs to be operable, not just
                clickable. Kept as a button rather than a Link because
                handleLogoRoute deliberately does nothing while onboarding is
                in progress, which an href cannot express. */}
            <button
              type='button'
              onClick={handleLogoRoute}
              aria-label={`${brandName} home`}
              className='cursor-pointer'
            >
              <img src={logoDark} alt={brandName} className='w-[12.5rem] h-auto' />
            </button>
            <SponsorLine className='text-[10px] leading-tight text-gray-500 mt-1 select-none' />
          </div>
        ) : (
          <div className='flex flex-col items-center'>
            <Avatar
              shape='square'
              size='5xl'
              name={user?.first_name + ' ' + user?.last_name}
              src={user?.avatar}
              className='mb-3'
            />
            <h5 className='text-primary-500 !font-medium mb-1'>
              {user?.first_name + ' ' + user?.last_name}
            </h5>
            <p className='text-body-500 text-base font-medium mb-0'>
              {user?.email}
            </p>
          </div>
        )}
        {isMobile && (
          <Xmark
            className='w-10 h-10 text-body-500 cursor-pointer absolute right-5 top-[-0.75rem]'
            onClick={() => setMenuOpen(false)}
          />
        )}
      </div>
      <div className='sm:px-8 grow overflow-y-auto w-full sm:max-w-full max-w-[16rem] mx-auto'>
        <ul className='space-y-3'>
          {/* Real anchors, not clickable list items. These were `<li onClick>`
              with no role, no href and no tabIndex, which left every navigation
              item in the app outside the tab order: measured on /dashboard,
              17 focusable elements on the page and not one of them was nav, so
              a keyboard-only user could not reach Discover, Matches, Inbox,
              Profile or Settings at all. Screen readers announced them as plain
              list items. An href also restores what people expect from a web
              sidebar: ctrl/middle-click to open in a new tab, copy link
              address, and the URL preview on hover. Link still routes
              client-side, so nothing about the SPA behaviour changes. */}
          {sidebar.map(item => {
            const isActive = pathname === item.path || pathname.startsWith(item.path + '/');
            return (
              <li key={item.label}>
                <Link
                  to={item.path}
                  data-tour-id={item.tourId}
                  aria-current={isActive ? 'page' : undefined}
                  className={`flex items-center font-medium p-4 cursor-pointer relative rounded-xl duration-300 ${isActive ? 'bg-heading-500 text-white' : ''}`}
                  onClick={() => setMenuOpen(false)}
                >
                  {isActive ? item.activeIcon : item.icon}
                  {item.label}
                  {item.path === routes.chat && unreadCount > 0 && (
                    <span className='ml-3 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs'>
                      {unreadCount}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
          {isMobile && (
            <li>
              {/* Same reason as the nav items above: this was a clickable `li`,
                  so the only way to log out on mobile was a pointer. */}
              <button
                type='button'
                className={`w-full flex items-center font-medium p-4 cursor-pointer relative rounded-xl duration-300 text-red-600`}
                onClick={() => {
                  logout();
                }}
              >
                <LogOut className='w-6 h-6 me-4' />
                <span>Logout</span>
              </button>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;
